<template>
  <div>

    <!-- ── Mobile Top Bar ── -->
    <div class="md:hidden fixed top-0 left-0 right-0 h-14 bg-secondary flex items-center justify-between px-4 z-40 shadow-lg">
      <div class="flex items-center gap-2">
        <img src="@/assets/abc-logo.png" alt="ABC Tax Advisory" class="h-7 w-auto object-contain" style="mix-blend-mode: screen;" />
        <div class="flex flex-col leading-tight">
          <span class="text-white text-sm font-bold">ABC Tax Advisory</span>
          <span class="text-primary text-xs font-medium">Admin Console</span>
        </div>
      </div>
      <button @click="mobileOpen = true" class="text-white/70 hover:text-white p-1">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>
    </div>

    <!-- ── Mobile overlay backdrop ── -->
    <transition name="fade">
      <div
        v-if="mobileOpen"
        class="md:hidden fixed inset-0 bg-black/50 z-40"
        @click="mobileOpen = false"/>
    </transition>

    <!-- ── Sidebar ── -->
    <transition name="slide">
      <aside
        v-show="mobileOpen || isDesktop"
        class="fixed md:static top-0 left-0 h-full md:h-screen w-64 bg-secondary flex flex-col flex-shrink-0 z-50 shadow-xl md:shadow-xl">

        <!-- Logo -->
        <div class="h-16 flex items-center px-6 border-b border-slate-700/50">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center gap-3">
              <img src="@/assets/abc-logo.png" alt="ABC Tax Advisory" class="h-8 w-auto object-contain" style="mix-blend-mode: screen;" />
              <div class="flex flex-col">
                <h1 class="text-white text-sm font-bold leading-tight tracking-tight">ABC Tax Advisory</h1>
                <span class="text-slate-400 text-xs font-medium">Admin Console</span>
              </div>
            </div>
            <!-- Close button — mobile only -->
            <button @click="mobileOpen = false" class="md:hidden text-white/50 hover:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 overflow-y-auto py-6 flex flex-col gap-1 px-3">
          <p class="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Main Menu</p>

          <a @click.prevent="navigate('clients')"
            class="sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 cursor-pointer"
            :class="{ 'active': currentPage === 'clients' }">
            <span class="material-symbols-outlined" style="font-size: 20px; font-variation-settings: 'FILL' 1;">group</span>
            <span class="text-sm font-medium">Client List</span>
          </a>

          <a @click.prevent="navigate('announcements')"
            class="sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 cursor-pointer"
            :class="{ 'active': currentPage === 'announcements' }">
            <span class="material-symbols-outlined" style="font-size: 20px;">campaign</span>
            <span class="text-sm font-medium">Announcements</span>
          </a>

          <a @click.prevent="navigate('documents')"
            class="sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 cursor-pointer"
            :class="{ 'active': currentPage === 'documents' }">
            <span class="material-symbols-outlined" style="font-size: 20px;">folder_open</span>
            <span class="text-sm font-medium">Document Vault</span>
          </a>

          <a @click.prevent="navigate('messages')"
            class="sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 cursor-pointer"
            :class="{ 'active': currentPage === 'messages' }">
            <div class="relative">
              <span class="material-symbols-outlined" style="font-size: 20px;">chat</span>
              <span v-if="unreadCount > 0"
                class="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-primary text-secondary text-[10px] font-bold flex items-center justify-center ring-2 ring-secondary">
                {{ unreadCount > 9 ? '9+' : unreadCount }}
              </span>
            </div>
            <span class="text-sm font-medium">Messages</span>
            <span v-if="unreadCount > 0" class="ml-auto px-1.5 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-bold">
              {{ unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </a>

          <p class="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-2">Management</p>

          <a @click.prevent="navigate('tasks')"
            class="sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 cursor-pointer"
            :class="{ 'active': currentPage === 'tasks' }">
            <span class="material-symbols-outlined" style="font-size: 20px;">task</span>
            <span class="text-sm font-medium">Tasks & Workflow</span>
          </a>
        </nav>

        <!-- User Profile / Logout -->
        <div class="p-4 border-t border-slate-700/50">
          <div @click="handleLogout"
            class="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-700/30 transition-colors cursor-pointer group">
            <div class="h-10 w-10 rounded-full bg-slate-600 flex items-center justify-center border-2 border-slate-500 flex-shrink-0">
              <span class="text-white text-sm font-bold">{{ adminInitials }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-white truncate">{{ adminName }}</p>
              <p class="text-xs text-slate-400 truncate">{{ adminEmail }}</p>
            </div>
            <span class="material-symbols-outlined text-slate-400 group-hover:text-white transition-colors flex-shrink-0"
              style="font-size: 20px;">logout</span>
          </div>
        </div>

      </aside>
    </transition>

    <!-- ── Mobile spacer so page content doesn't go under the top bar ── -->
    <div class="md:hidden h-14 flex-shrink-0"></div>

  </div>
</template>

<script>
export default {
  name: 'AppSidebar',

  props: {
    currentPage: { type: String, default: '' }
  },

  data() {
    return {
      adminName: 'Admin User',
      adminEmail: '',
      adminInitials: 'AU',
      unreadCount: 0,
      unreadInterval: null,
      mobileOpen: false,
      isDesktop: window.innerWidth >= 768
    };
  },

  mounted() {
    this.loadUser();
    this.loadUnreadCount();
    this.unreadInterval = setInterval(() => this.loadUnreadCount(), 60000);
    this._onResize = () => { this.isDesktop = window.innerWidth >= 768; };
    window.addEventListener('resize', this._onResize);
    this._onMessagesRead = () => this.loadUnreadCount();
    window.addEventListener('messages-read', this._onMessagesRead);
  },

  beforeUnmount() {
    if (this.unreadInterval) clearInterval(this.unreadInterval);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('messages-read', this._onMessagesRead);
  },

  methods: {
    loadUser() {
      try {
        const user = this.$sharepoint.getCurrentUser();
        if (user) {
          this.adminName = user.name || user.username || 'Admin User';
          this.adminEmail = user.username || '';
          this.adminInitials = this.getInitials(this.adminName);
        }
      } catch {
        this.adminName = 'Admin User';
        this.adminInitials = 'AU';
      }
    },

    async loadUnreadCount() {
      try { this.unreadCount = await this.$sharepoint.getUnreadMessageCount(); }
      catch { this.unreadCount = 0; }
    },

    getInitials(name) {
      if (!name) return 'AU';
      const parts = name.trim().split(' ');
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },

    navigate(page) {
      const routes = {
        clients: '/admin/clients',
        announcements: '/admin/announcements',
        documents: '/admin/documents',
        messages: '/admin/messages',
        tasks: '/admin/tasks',
        settings: '/admin/settings'
      };
      const target = routes[page];
      if (target && this.$route.path !== target) this.$router.push(target);
      this.mobileOpen = false;
    },

    async handleLogout() {
      try { await this.$sharepoint.logout(); }
      catch (e) { console.error('Logout error:', e); }
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.sidebar-link { transition: all 0.2s ease-in-out; }
.sidebar-link:hover { background-color: rgba(255,255,255,0.05); color: #ffffff; }
.sidebar-link.active { background-color: rgba(230,175,45,0.15); color: #e6af2d; border-right: 3px solid #e6af2d; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.slide-enter-active, .slide-leave-active { transition: transform 0.25s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(-100%); }
</style>